'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Plus, Download, Pencil, Trash2, Wrench, KeyRound,
  AlertTriangle, X, Camera, Star, Upload,
} from 'lucide-react';
import type { Vehicle, VehicleCategory, VehicleStatus } from '@/types';
import {
  MOROCCAN_LOCATIONS, daysUntil,
  complianceState, vidangeState,
} from '@/lib/morocco';
import { apiAsset } from '@/config';
import { currencySymbol, formatMoney, normalizeCurrency } from '@/lib/currency';
import type { ComplianceState } from '@/lib/morocco';
import { parseVehiclePage } from '@/lib/booking/laravel-mirror';
import { useDeskSummary } from '@/lib/desk/summary';
import { listPath } from '@/lib/query/page';
import { useDebouncedSearch, useSectionParams } from '@/lib/query/table-params';
import { useTenantQuery } from '@/lib/query/useTenantQuery';
import { DeskTable } from './DeskTable';
import { ConfirmDialog } from '@/components/dashboard/ConfirmDialog';
import { SlideOver } from './SlideOver';
import { VehicleBlocks } from './VehicleBlocks';
import { StatusChip, complianceDotClass, useDeskToast, vehicleTone } from './desk-feedback';
import { RecordPager } from './RecordPager';

interface FleetModuleProps {
  onAddVehicle: (v: Vehicle) => void | Promise<Vehicle | null>;
  onUpdateVehicle: (v: Vehicle) => void;
  onUpdateVehicleStatus: (id: string, status: VehicleStatus) => void;
  onApplyStatus?: (ids: string[], status: VehicleStatus) => Promise<boolean>;
  loadError?: boolean;
  onRetry?: () => void;
  onDeleteVehicle: (id: string) => void | Promise<true | string>;
  canDelete: boolean;
  maintenanceOnly?: boolean;
  currency?: string | null;
  headerQuery?: string;
  locations?: string[];
}

const CATEGORIES: VehicleCategory[] = ['Economy', 'SUV', 'Luxury', 'Convertible', 'Electric', 'Van'];
const STATUS_OPTIONS = ['All', 'Available', 'Rented', 'Maintenance', 'Reserved'] as const;
const FLEET_COLUMNS = [
  { id: 'vehicle', label: 'Vehicle', sortable: true },
  { id: 'plate', label: 'Plate', sortable: true },
  { id: 'category', label: 'Category' },
  { id: 'location', label: 'Location' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'status', label: 'Status', sortable: true },
];
const FLEET_SORT: Record<string, string> = {
  vehicle: 'name',
  plate: 'license_plate',
  status: 'status',
};

const inputCls = 'w-full rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink outline-none focus:border-brand';
const labelCls = 'mb-1 block text-xs font-semibold text-muted';

function statusLabel(status: VehicleStatus): string {
  if (status === 'available') return 'Available';
  if (status === 'rented') return 'On Rent';
  if (status === 'maintenance') return 'Maintenance';
  return 'Reserved';
}

function hasComplianceIssue(v: Vehicle): boolean {
  return (
    complianceState(v.visiteTechniqueExpiry) !== 'ok' ||
    complianceState(v.assuranceExpiry) !== 'ok' ||
    complianceState(v.vignetteExpiry) !== 'ok' ||
    vidangeState(v) !== 'ok'
  );
}

interface FormState {
  name: string; brand: string; model: string; category: VehicleCategory;
  pricePerDay: string; seats: string; transmission: 'Automatic' | 'Manual';
  fuelType: Vehicle['fuelType']; colorName: string; year: string;
  licensePlate: string; mileage: string; location: string;
  visiteTechniqueExpiry: string; assuranceExpiry: string; vignetteExpiry: string;
  lastVidangeKm: string; nextVidangeKm: string;
  listed: boolean;
}

const emptyForm: FormState = {
  name: '', brand: '', model: '', category: 'Economy',
  pricePerDay: '', seats: '5', transmission: 'Manual',
  fuelType: 'Diesel', colorName: '', year: String(new Date().getFullYear()),
  licensePlate: '', mileage: '', location: MOROCCAN_LOCATIONS[0] ?? '',
  visiteTechniqueExpiry: '', assuranceExpiry: '', vignetteExpiry: '',
  lastVidangeKm: '', nextVidangeKm: '',
  listed: true,
};

function toForm(v: Vehicle): FormState {
  return {
    name: v.name, brand: v.brand, model: v.model, category: v.category,
    pricePerDay: String(v.pricePerDay), seats: String(v.seats),
    transmission: v.transmission, fuelType: v.fuelType,
    colorName: v.colorName ?? '', year: String(v.year),
    licensePlate: v.licensePlate, mileage: String(v.mileage),
    location: v.location ?? MOROCCAN_LOCATIONS[0] ?? '',
    visiteTechniqueExpiry: v.visiteTechniqueExpiry ?? '',
    assuranceExpiry: v.assuranceExpiry ?? '',
    vignetteExpiry: v.vignetteExpiry ?? '',
    lastVidangeKm: v.lastVidangeKm != null ? String(v.lastVidangeKm) : '',
    nextVidangeKm: v.nextVidangeKm != null ? String(v.nextVidangeKm) : '',
    listed: v.listed !== false,
  };
}

export function FleetModule({
  onAddVehicle, onUpdateVehicle, onUpdateVehicleStatus, onApplyStatus,
  onRetry,
  onDeleteVehicle, canDelete, maintenanceOnly = false, currency, locations = MOROCCAN_LOCATIONS,
}: FleetModuleProps) {
  const cur = currency ?? 'MAD';
  const section = maintenanceOnly ? 'bay' : 'fleet';
  const table = useSectionParams(section);
  const search = useDebouncedSearch(section, table.search, table.write);
  const statusFilter = table.read('status', 'All');
  const typeFilter = table.read('category', 'All');
  const locationFilter = table.read('location', 'All');
  const sortId = Object.entries(FLEET_SORT).find(([, column]) => column === (table.sort || 'created_at'))?.[0] ?? '';
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const notify = useDeskToast();
  const [batchBusy, setBatchBusy] = useState(false);
  const limits = useDeskSummary();
  const fleetQuery = useTenantQuery(
    ['vehicles', section, table.page, table.search, table.sort, table.direction, statusFilter, typeFilter, locationFilter],
    listPath('/api/staff/vehicles', {
      page: table.page,
      perPage: 20,
      sort: table.sort || 'created_at',
      direction: table.direction || 'desc',
      q: table.search,
      status: statusFilter === 'All' ? undefined : statusFilter.toLowerCase(),
      category: typeFilter,
      location: locationFilter,
      attention: maintenanceOnly ? '1' : undefined,
    }),
    parseVehiclePage,
  );
  const vehicles = (fleetQuery.data?.data ?? []) as Vehicle[];
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Vehicle | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  // Photo manager state (dashboard multi-image upload).
  const [photos, setPhotos] = useState<string[]>([]);
  const [pending, setPending] = useState<{ file: File; preview: string }[]>([]);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<'name' | 'plate', string>>>({});
  const [savingVehicle, setSavingVehicle] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState<Vehicle | null>(null);
  const [deletePending, setDeletePending] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Revoke preview URLs on unmount only (removals revoke individually).
  const pendingFiles = useMemo(() => pending.map((p) => p.file), [pending]);
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  });
  useEffect(() => {
    return () => {
      pendingRef.current.forEach((p) => URL.revokeObjectURL(p.preview));
    };
  }, []);

  const compliance = limits.data?.summary.compliance;
  const stats = {
    visite: compliance?.visite ?? 0,
    assurance: compliance?.assurance ?? 0,
    vignette: compliance?.vignette ?? 0,
    vidange: compliance?.vidange ?? 0,
  };
  const total = fleetQuery.data?.meta.total ?? 0;
  const filtersActive = table.search !== '' || statusFilter !== 'All' || typeFilter !== 'All' || locationFilter !== 'All';
  const listKind = fleetQuery.isLoading ? 'loading' as const
    : fleetQuery.isError && !fleetQuery.data ? 'error' as const
    : total === 0 && filtersActive ? 'filtered' as const
    : total === 0 ? 'empty' as const
    : 'ready' as const;
  const pageItems = vehicles;
  const visibleIds = pageItems.map((vehicle) => vehicle.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  function toggleVisibleVehicles() {
    setSelected((current) => {
      const next = new Set(current);
      if (allVisibleSelected) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function toggleVehicle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function applyFleetBatch(status: VehicleStatus) {
    if (!onApplyStatus || batchBusy) return;
    const ids = [...selected];
    if (ids.length === 0) return;
    setBatchBusy(true);
    const saved = await onApplyStatus(ids, status);
    setBatchBusy(false);
    if (!saved) {
      notify('Could not update those vehicles.', 'error');
      return;
    }
    setSelected(new Set());
    notify(status === 'available' ? `Marked ${ids.length} available.` : `Sent ${ids.length} to maintenance.`);
  }

  function openAdd() {
    setEditing(null);
    setForm({ ...emptyForm, location: locations[0] ?? '' });
    setPhotos([]);
    setPending([]);
    setPhotoError(null);
    setModalOpen(true);
  }

  function openEdit(v: Vehicle) {
    setEditing(v);
    setForm(toForm(v));
    setPhotos(Array.isArray(v.galleryImages) ? [...v.galleryImages] : []);
    setPending([]);
    setPhotoError(null);
    setModalOpen(true);
  }

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  // Upload files to an existing (server-side) vehicle id. Returns updated gallery.
  async function uploadPhotos(vehicleId: string, files: File[]): Promise<string[] | null> {
    const fd = new FormData();
    files.forEach((f) => fd.append('photos', f));
    setUploading(true);
    setPhotoError(null);
    try {
      const res = await fetch(`/api/vehicles/${encodeURIComponent(vehicleId)}/photos`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhotoError(data.error ?? 'Upload failed.');
        return null;
      }
      const updated = data.vehicle as Vehicle;
      onUpdateVehicle(updated);
      setPhotos(Array.isArray(updated.galleryImages) ? [...updated.galleryImages] : []);
      // Keep the edit form pointed at the fresh record (cover may change).
      setEditing((cur) => (cur && cur.id === updated.id ? updated : cur));
      return updated.galleryImages ?? [];
    } catch {
      setPhotoError('Network error during upload.');
      return null;
    } finally {
      setUploading(false);
    }
  }

  function handlePickFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const picked = [...files].filter((f) => f.size > 0);
    if (picked.length === 0) return;
    if (editing) {
      // Existing vehicle → upload immediately.
      void uploadPhotos(editing.id, picked);
    } else {
      // New vehicle → stash, upload right after creation in handleSave.
      setPending((cur) =>
        [...cur, ...picked.map((file) => ({ file, preview: URL.createObjectURL(file) }))].slice(0, 8)
      );
    }
  }

  function removePending(index: number) {
    setPending((cur) => {
      const target = cur[index];
      if (target) URL.revokeObjectURL(target.preview);
      return cur.filter((_, j) => j !== index);
    });
  }

  async function handleDeletePhoto(url: string) {
    if (!editing) {
      setPhotos((cur) => cur.filter((u) => u !== url));
      return;
    }
    setUploading(true);
    setPhotoError(null);
    try {
      const res = await fetch(
        `/api/vehicles/${encodeURIComponent(editing.id)}/photos?url=${encodeURIComponent(url)}`,
        { method: 'DELETE' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPhotoError(data.error ?? 'Delete failed.');
        return;
      }
      const updated = data.vehicle as Vehicle;
      onUpdateVehicle(updated);
      setPhotos(Array.isArray(updated.galleryImages) ? [...updated.galleryImages] : []);
      setEditing((cur) => (cur && cur.id === updated.id ? updated : cur));
    } catch {
      setPhotoError('Network error.');
    } finally {
      setUploading(false);
    }
  }

  function handleSetPrimary(url: string) {
    const base = editing;
    if (!base) return;
    onUpdateVehicle({ ...base, galleryImages: photos, imageUrl: url });
    setEditing({ ...base, galleryImages: photos, imageUrl: url });
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors: Partial<Record<'name' | 'plate', string>> = {};
    if (!form.name.trim()) nextErrors.name = 'Enter a name.';
    if (!form.licensePlate.trim()) nextErrors.plate = 'Enter the plate.';
    setFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setSavingVehicle(true);
    try {
    const num = (s: string, fb: number) => {
      const n = Number(s);
      return Number.isFinite(n) && s.trim() !== '' ? n : fb;
    };
    const optNum = (s: string) => (s.trim() === '' ? undefined : Number(s));
    if (editing) {
      onUpdateVehicle({
        ...editing,
        name: form.name.trim(), brand: form.brand.trim(), model: form.model.trim(),
        category: form.category, pricePerDay: num(form.pricePerDay, editing.pricePerDay),
        seats: num(form.seats, editing.seats), transmission: form.transmission,
        fuelType: form.fuelType, colorName: form.colorName.trim(),
        year: num(form.year, editing.year), licensePlate: form.licensePlate.trim(),
        mileage: num(form.mileage, editing.mileage), location: form.location,
        visiteTechniqueExpiry: form.visiteTechniqueExpiry || undefined,
        assuranceExpiry: form.assuranceExpiry || undefined,
        vignetteExpiry: form.vignetteExpiry || undefined,
        lastVidangeKm: optNum(form.lastVidangeKm), nextVidangeKm: optNum(form.nextVidangeKm),
        listed: form.listed,
      });
    } else {
      const v: Vehicle = {
        id: `car-${Date.now()}`, name: form.name.trim(), brand: form.brand.trim() || form.name.trim(),
        model: form.model.trim() || form.name.trim(), category: form.category,
        pricePerDay: num(form.pricePerDay, 300), seats: num(form.seats, 5),
        transmission: form.transmission, bags: 2, fuelType: form.fuelType, mpg: '',
        rating: 5, reviewsCount: 1, licensePlate: form.licensePlate.trim(),
        status: 'available', mileage: num(form.mileage, 0), features: [],
        imageType: 'illustration', colorName: form.colorName.trim(),
        year: num(form.year, new Date().getFullYear()), location: form.location,
        visiteTechniqueExpiry: form.visiteTechniqueExpiry || undefined,
        assuranceExpiry: form.assuranceExpiry || undefined,
        vignetteExpiry: form.vignetteExpiry || undefined,
        lastVidangeKm: optNum(form.lastVidangeKm), nextVidangeKm: optNum(form.nextVidangeKm),
        listed: form.listed,
      };
      const created = await onAddVehicle(v);
      if (!created) return;
      if (pendingFiles.length > 0) {
        await uploadPhotos(created.id, pendingFiles);
        setPending([]);
      }
    }
    setModalOpen(false);
    notify(editing ? 'Vehicle updated.' : 'Vehicle saved.');
    } finally {
      setSavingVehicle(false);
    }
  }

  function handleExport() {
    const header = 'ID,Vehicle,Matricule,Category,PriceDH,Status,Mileage,Location,Visite,Assurance,Vignette,NextVidange';
    const rows = vehicles.map((v) =>
      [v.id, `"${v.name}"`, v.licensePlate, v.category, v.pricePerDay, v.status,
        v.mileage, `"${v.location ?? ''}"`, v.visiteTechniqueExpiry ?? '',
        v.assuranceExpiry ?? '', v.vignetteExpiry ?? '', v.nextVidangeKm ?? ''].join(',')
    );
    const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'fleet-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-ink">{maintenanceOnly ? 'Maintenance Bay' : 'Fleet Management'}</h2>
          <p className="text-xs text-muted">
            {maintenanceOnly ? 'Vehicles with compliance attention needed' : `${vehicles.length} vehicles · Prices in ${normalizeCurrency(cur)}`}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleExport} className="flex items-center gap-1.5 rounded-xl border border-line bg-card px-3 py-2 text-xs font-semibold text-ink hover:bg-shell">
            <Download size={14} /> Export CSV
          </button>
          <button onClick={openAdd} className="flex items-center gap-1.5 rounded-xl bg-ink px-3 py-2 text-xs font-semibold text-shell hover:brightness-110">
            <Plus size={14} /> Add Vehicle
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: 'Visite expired', count: stats.visite },
          { label: 'Assurance expired', count: stats.assurance },
          { label: 'Vignette expired', count: stats.vignette },
          { label: 'Vidange overdue', count: stats.vidange },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-2 rounded-2xl border border-line bg-card p-3">
            <span className={`flex h-8 w-8 items-center justify-center rounded-xl ${s.count > 0 ? 'bg-danger/10 text-danger' : 'bg-success/10 text-success'}`}>
              <AlertTriangle size={16} />
            </span>
            <div>
              <p className="text-lg font-bold text-ink">{s.count}</p>
              <p className="text-xs text-muted">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <DeskTable
        storageKey={maintenanceOnly ? 'fleetava-desk-bay-columns' : 'fleetava-desk-fleet-columns'}
        columns={FLEET_COLUMNS}
        search={
          <input
            value={search.draft}
            onChange={(event) => search.onSearch(event.target.value)}
            placeholder="Search name, plate, or brand"
            aria-label="Search the fleet"
            className="w-full max-w-sm rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink"
          />
        }
        refreshing={fleetQuery.isFetching && Boolean(fleetQuery.data)}
        refreshError={fleetQuery.isError && Boolean(fleetQuery.data)}
        sort={sortId ? { id: sortId, direction: table.direction === 'asc' ? 'asc' : 'desc' } : undefined}
        onSort={(columnId) => {
          const column = FLEET_SORT[columnId];
          if (!column) return;
          const nextDirection = table.sort === column && table.direction === 'asc' ? 'desc' : 'asc';
          table.write({ sort: column, dir: nextDirection });
        }}
        filters={
          <div className="flex flex-wrap gap-2">
            <select value={statusFilter} onChange={(event) => table.write({ status: event.target.value })} aria-label="Status" className="rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink">
              {STATUS_OPTIONS.map((status) => <option key={status} value={status}>{status === 'All' ? 'All statuses' : status}</option>)}
            </select>
            <select value={typeFilter} onChange={(event) => table.write({ category: event.target.value })} aria-label="Category" className="rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink">
              <option value="All">All categories</option>
              {CATEGORIES.map((category) => <option key={category} value={category}>{category}</option>)}
            </select>
            <select value={locationFilter} onChange={(event) => table.write({ location: event.target.value })} aria-label="Location" className="rounded-xl border border-line bg-shell px-3 py-2 text-xs text-ink">
              <option value="All">All locations</option>
              {locations.map((location) => <option key={location} value={location}>{location}</option>)}
            </select>
          </div>
        }
        selectedCount={selected.size}
        allVisibleSelected={allVisibleSelected}
        onToggleVisible={toggleVisibleVehicles}
        onClearSelection={() => setSelected(new Set())}
        batch={
          <>
            <button type="button" disabled={batchBusy} onClick={() => void applyFleetBatch('available')} className="rounded-full bg-success px-3 py-1 font-semibold text-slate-950 disabled:opacity-50">Mark available</button>
            <button type="button" disabled={batchBusy} onClick={() => void applyFleetBatch('maintenance')} className="rounded-full bg-warning px-3 py-1 font-semibold text-slate-950 disabled:opacity-50">Maintenance</button>
          </>
        }
        listState={listKind}
        onRetry={() => { void fleetQuery.refetch(); onRetry?.(); }}
        emptyTitle={maintenanceOnly ? 'No vehicles need the bay.' : 'No vehicles yet.'}
        filteredTitle="No rows match these filters."
        errorTitle="The fleet could not be loaded."
        notice={null}
        onDismissNotice={() => undefined}
      >
        {(shown) => pageItems.map((v) => {
              const visite = complianceState(v.visiteTechniqueExpiry);
              const assurance = complianceState(v.assuranceExpiry);
              const vignette = complianceState(v.vignetteExpiry);
              const vidange = vidangeState(v);
              const dots: { k: string; s: ComplianceState; title: string }[] = [
                { k: 'V', s: visite, title: `Visite: ${v.visiteTechniqueExpiry ?? '—'} (${daysUntil(v.visiteTechniqueExpiry) ?? '?' }d)` },
                { k: 'A', s: assurance, title: `Assurance: ${v.assuranceExpiry ?? '—'} (${daysUntil(v.assuranceExpiry) ?? '?'}d)` },
                { k: 'G', s: vignette, title: `Vignette: ${v.vignetteExpiry ?? '—'} (${daysUntil(v.vignetteExpiry) ?? '?'}d)` },
                { k: 'O', s: vidange, title: `Vidange: next ${v.nextVidangeKm ?? '—'} km (odo ${v.mileage})` },
              ];
              return (
                <tr key={v.id} className="border-t border-line">
                  <td className="px-3 py-2">
                    <input type="checkbox" checked={selected.has(v.id)} onChange={() => toggleVehicle(v.id)} aria-label={`Select ${v.name}`} />
                  </td>
                  {shown('vehicle') && <td className="px-3 py-2">
                    <div className="flex items-center gap-2.5">
                      {(v.galleryImages?.[0] ?? v.imageUrl) ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={v.galleryImages?.[0] ?? v.imageUrl}
                          alt={v.name}
                          className="h-10 w-14 shrink-0 rounded-lg border border-line object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <span className="flex h-10 w-14 shrink-0 items-center justify-center rounded-lg border border-dashed border-line bg-shell text-muted">
                          <Camera size={14} />
                        </span>
                      )}
                      <div className="min-w-0">
                        <p className="font-bold text-ink">{v.name}</p>
                        <p className="text-muted">
                          {v.id}
                          {(v.galleryImages?.length ?? 0) > 0 && (
                            <span className="ml-1 rounded-md bg-shell px-1.5 py-0.5 text-[10px] font-bold text-muted">
                              {(v.galleryImages?.length ?? 0)} photo{(v.galleryImages?.length ?? 0) > 1 ? 's' : ''}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  </td>}
                  {shown('plate') && <td className="px-3 py-2 text-muted">
                    <p className="font-semibold text-ink">{v.licensePlate}</p>
                    <p>{v.seats} seats · {v.transmission} · {v.colorName} · {v.mileage.toLocaleString()} km</p>
                  </td>}
                  {shown('category') && <td className="px-3 py-2"><span className="rounded-lg bg-shell px-2 py-1 font-semibold">{v.category}</span></td>}
                  {shown('location') && <td className="px-3 py-2">{v.location ?? '—'}</td>}
                  {shown('compliance') && <td className="px-3 py-2">
                    <div className="flex gap-1.5">
                      {dots.map((d) => (
                        <span key={d.k} title={d.title} className={`flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-slate-950 ${complianceDotClass(d.s)}`}>{d.k}</span>
                      ))}
                    </div>
                    {dots.some((dot) => dot.k !== 'O' && (dot.s === 'soon' || dot.s === 'expired')) && (
                      <p className={`mt-1 max-w-[220px] text-[10px] font-semibold ${dots.some((dot) => dot.k !== 'O' && dot.s === 'expired') ? 'text-danger' : 'text-warning'}`}>
                        {dots.filter((dot) => dot.k !== 'O' && (dot.s === 'soon' || dot.s === 'expired')).map((dot) => dot.title).join(' · ')}
                      </p>
                    )}
                  </td>}
                  {shown('status') && (
                    <td className="px-3 py-2">
                      <StatusChip tone={vehicleTone(v.status)}>{statusLabel(v.status)}</StatusChip>
                      {v.listed === false && <span className="ml-1 text-[10px] font-semibold text-muted">Unpublished</span>}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <button title="Edit" onClick={() => openEdit(v)} className="rounded-lg border border-line p-1.5 text-muted hover:bg-shell"><Pencil size={14} /></button>
                      <button title={v.status === 'rented' ? 'Mark returned' : 'Toggle rent'}
                        onClick={() => onUpdateVehicleStatus(v.id, v.status === 'rented' ? 'available' : 'rented')}
                        className="rounded-lg border border-line p-1.5 text-muted hover:bg-shell"><KeyRound size={14} /></button>
                      <button title="Send to bay" onClick={() => onUpdateVehicleStatus(v.id, 'maintenance')}
                        className="rounded-lg border border-line p-1.5 text-muted hover:bg-shell"><Wrench size={14} /></button>
                      {canDelete && (
                        <button title="Delete" onClick={() => { setDeleteError(null); setVehicleToDelete(v); }}
                          className="rounded-lg border border-danger/40 p-1.5 text-danger hover:bg-danger/10"><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
      </DeskTable>

      <RecordPager
        page={fleetQuery.data?.meta.page ?? table.page}
        pages={Math.max(1, Math.ceil(total / (fleetQuery.data?.meta.perPage || 20)))}
        total={total}
        onPage={(next) => table.write({ page: String(next) }, false)}
      />

      <SlideOver
        open={modalOpen}
        title={editing ? 'Edit vehicle' : 'New vehicle'}
        onClose={() => { if (!savingVehicle) setModalOpen(false); }}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setModalOpen(false)} disabled={savingVehicle} className="rounded-xl border border-line px-4 py-2 text-xs font-semibold text-ink disabled:opacity-50">Cancel</button>
            <button type="submit" form="vehicle-form" disabled={savingVehicle || uploading} className="rounded-xl bg-brand px-4 py-2 text-xs font-semibold text-slate-950 disabled:opacity-50">{savingVehicle ? 'Saving…' : 'Save vehicle'}</button>
          </div>
        }
      >
            <form id="vehicle-form" onSubmit={handleSave} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Name *</label><input value={form.name} onChange={(e) => set('name', e.target.value)} className={inputCls} />{fieldErrors.name && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.name}</p>}</div>
              <div><label className={labelCls}>Matricule *</label><input value={form.licensePlate} onChange={(e) => set('licensePlate', e.target.value)} className={inputCls} placeholder="12345-A-6" />{fieldErrors.plate && <p className="mt-1 text-[11px] font-semibold text-danger">{fieldErrors.plate}</p>}</div>
              <div><label className={labelCls}>Brand</label><input value={form.brand} onChange={(e) => set('brand', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Model</label><input value={form.model} onChange={(e) => set('model', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Category</label>
                <select value={form.category} onChange={(e) => set('category', e.target.value as VehicleCategory)} className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div><label className={labelCls}>Price / day ({currencySymbol(cur)})</label><input type="number" min={0} value={form.pricePerDay} onChange={(e) => set('pricePerDay', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Seats</label><input type="number" min={2} max={9} value={form.seats} onChange={(e) => set('seats', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Transmission</label>
                <select value={form.transmission} onChange={(e) => set('transmission', e.target.value as FormState['transmission'])} className={inputCls}>
                  <option value="Manual">Manual</option><option value="Automatic">Automatic</option>
                </select>
              </div>
              <div><label className={labelCls}>Fuel</label>
                <select value={form.fuelType} onChange={(e) => set('fuelType', e.target.value as FormState['fuelType'])} className={inputCls}>
                  <option value="Diesel">Diesel</option><option value="Gasoline">Gasoline</option>
                  <option value="Hybrid">Hybrid</option><option value="Electric">Electric</option>
                </select>
              </div>
              <div><label className={labelCls}>Color</label><input value={form.colorName} onChange={(e) => set('colorName', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Year</label><input type="number" value={form.year} onChange={(e) => set('year', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Mileage compteur (km)</label><input type="number" min={0} value={form.mileage} onChange={(e) => set('mileage', e.target.value)} className={inputCls} /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input id="vehicle-listed" type="checkbox" checked={form.listed} onChange={(e) => set('listed', e.target.checked)} className="h-4 w-4 accent-[#FF8A00]" />
                <label htmlFor="vehicle-listed" className="text-xs font-semibold text-ink">Published on the website</label>
              </div>
              {editing && <VehicleBlocks vehicleId={editing.id} />}
              <div className="col-span-2"><label className={labelCls}>Location</label>
                <select value={form.location} onChange={(e) => set('location', e.target.value)} className={inputCls}>
                  {locations.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="col-span-2 rounded-2xl border border-line bg-shell/60 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <label className="flex items-center gap-1.5 text-xs font-bold text-ink">
                    <Camera size={14} className="text-[#FF8A00]" />
                    Photos ({photos.length + pending.length})
                    <span className="font-normal text-muted">· JPG/PNG/WebP, ≤5MB</span>
                  </label>
                  <label className="flex cursor-pointer items-center gap-1.5 rounded-xl bg-ink px-3 py-1.5 text-xs font-semibold text-shell hover:brightness-110">
                    <Upload size={13} />
                    {uploading ? 'Uploading…' : editing ? 'Add photos' : 'Select photos'}
                    <input
                      type="file" accept="image/jpeg,image/png,image/webp" multiple
                      className="hidden" disabled={uploading}
                      onChange={(e) => { handlePickFiles(e.target.files); e.target.value = ''; }}
                    />
                  </label>
                </div>
                {!editing && pending.length > 0 && (
                  <p className="mb-2 text-[11px] text-muted">Will upload on Save (new vehicle).</p>
                )}
                {photoError && (
                  <p className="mb-2 rounded-xl bg-danger/10 px-3 py-2 text-[11px] font-semibold text-danger">{photoError}</p>
                )}
                {(photos.length > 0 || pending.length > 0) ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((url) => {
                      const isPrimary = (editing?.imageUrl || '') === url;
                      return (
                        <div key={url} className={`group relative overflow-hidden rounded-xl border-2 ${isPrimary ? 'border-[#FF8A00]' : 'border-transparent'}`}>
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={apiAsset(url)} alt="Vehicle" className="h-16 w-full object-cover" loading="lazy" />
                          <div className="absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                            <button type="button" title={isPrimary ? 'Cover photo' : 'Set as cover'}
                              onClick={() => handleSetPrimary(url)}
                              className={`rounded-lg p-1 shadow ${isPrimary ? 'bg-[#FF8A00] text-slate-950' : 'bg-white/90 text-slate-950 hover:text-[#FF8A00]'}`}>
                              <Star size={12} fill={isPrimary ? 'currentColor' : 'none'} />
                            </button>
                            <button type="button" title="Delete photo" onClick={() => void handleDeletePhoto(url)}
                              className="rounded-lg bg-white/90 p-1 text-slate-950 shadow hover:text-danger">
                              <Trash2 size={12} />
                            </button>
                          </div>
                          {isPrimary && (
                            <span className="absolute bottom-1 left-1 rounded-md bg-[#FF8A00] px-1.5 py-0.5 text-[9px] font-bold text-slate-950">COVER</span>
                          )}
                        </div>
                      );
                    })}
                    {pending.map((p, i) => (
                      <div key={p.preview} className="relative overflow-hidden rounded-xl border-2 border-dashed border-line">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={p.preview} alt="Pending" className="h-16 w-full object-cover opacity-80" />
                        <button type="button" title="Remove"
                          onClick={() => removePending(i)}
                          className="absolute right-1 top-1 rounded-lg bg-white/90 p-1 text-slate-950 shadow hover:text-danger">
                          <X size={12} />
                        </button>
                        <span className="absolute bottom-1 left-1 rounded-md bg-black/70 px-1.5 py-0.5 text-[9px] font-bold text-white">NEW</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted">No photos yet — add exterior, interior and dashboard shots.</p>
                )}
              </div>
              <div><label className={labelCls}>Visite technique expiry</label><input type="date" value={form.visiteTechniqueExpiry} onChange={(e) => set('visiteTechniqueExpiry', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Assurance expiry</label><input type="date" value={form.assuranceExpiry} onChange={(e) => set('assuranceExpiry', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Vignette expiry</label><input type="date" value={form.vignetteExpiry} onChange={(e) => set('vignetteExpiry', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Last vidange (km)</label><input type="number" min={0} value={form.lastVidangeKm} onChange={(e) => set('lastVidangeKm', e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Next vidange (km)</label><input type="number" min={0} value={form.nextVidangeKm} onChange={(e) => set('nextVidangeKm', e.target.value)} className={inputCls} /></div>
            </form>
      </SlideOver>
      <ConfirmDialog
        open={vehicleToDelete !== null}
        title={vehicleToDelete ? `Delete ${vehicleToDelete.name}?` : 'Delete vehicle?'}
        description="The vehicle leaves this fleet."
        confirmLabel="Delete vehicle"
        pendingLabel="Deleting…"
        pending={deletePending}
        error={deleteError}
        onCancel={() => {
          if (deletePending) return;
          setVehicleToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={async () => {
          if (!vehicleToDelete) return;
          setDeletePending(true);
          setDeleteError(null);
          try {
            const result = await Promise.resolve(onDeleteVehicle(vehicleToDelete.id));
            if (result !== true) {
              setDeleteError(typeof result === 'string' ? result : 'Could not delete this vehicle.');
              return;
            }
            setVehicleToDelete(null);
          } finally {
            setDeletePending(false);
          }
        }}
      />
    </div>
  );
}
